// Contrato entre o worker e o app: o mesmo esquema valida a resposta no servidor
// (testes) e no navegador (lib/api).
// zod/mini: a mesma validação com uma fração do tamanho no bundle do navegador.
import * as z from 'zod/mini'
import { AreaLogada, SalvarTelaDeLogin, TelaDeLogin } from './ambiente'
import { Glossario } from './glossario'
import { Capacidades, Modo } from './modo'

export * from './glossario'

/**
 * As peles da área do aluno. `sob-medida` é o quinto id fixo: a pele feita sob encomenda para uma instituição (pasta
 * `skins/<slug>/`, embutida só no build daquela instância). Só vale com a capacidade `peleSobMedida` ligada pelo
 * superadmin e a skin no build; sem uma das duas, a área do aluno cai no padrão (ver `modoAlunoEfetivo`).
 */
export const MODOS_ALUNO = ['moderno', 'revista', 'neoclassico', 'digital', 'sob-medida'] as const
export const ModoAluno = z.enum(MODOS_ALUNO)
export type ModoAluno = z.infer<typeof ModoAluno>

export const Saude = z.object({ ok: z.literal(true), versao: z.string() })

// ---------------------------------------------------------------- instância e configuração (recorte do MVP, docs/04)
const hex = z.string().check(z.regex(/^#[0-9a-fA-F]{6}$/, 'cor em hexadecimal, como #2f5bea'))
/** Regra do tema: travado em claro, travado em escuro ou escolha de cada pessoa, com o padrão. */
export const TemaConfig = z.object({
  modo: z.enum(['escolha', 'claro', 'escuro']),
  padrao: z.enum(['claro', 'escuro']),
})
export type TemaConfig = z.infer<typeof TemaConfig>
export const Marca = z.object({
  nome: z.string(),
  logoUrl: z.nullable(z.string()),
  /** o ícone da aba do navegador e o ícone quadrado (tela inicial do celular, menus compactos) */
  faviconUrl: z.optional(z.nullable(z.string())),
  iconeUrl: z.optional(z.nullable(z.string())),
  corPrimaria: hex,
  corSobrePrimaria: hex,
  /** segunda cor (selos, progresso, realces); nula = a principal */
  corDestaque: z.nullable(hex),
  /** a máscara translúcida sobre as imagens; nula = a principal */
  corOverlay: z.nullable(hex),
  /** o logo no cabeçalho: altura normal ou reduzida (logos que pedem mais respiro) e à esquerda ou centralizado */
  logoTamanho: z.optional(z.enum(['normal', 'reduzido'])),
  logoPosicao: z.optional(z.enum(['esquerda', 'centro'])),
})
export type Marca = z.infer<typeof Marca>
/** Paleta de fundo (aw-ava docs/17, K11): Padrão, Conforto ou Personalizada, com versão clara e escura. A pele nunca a sobrepõe. */
const CoresFundo = z.object({ bg: hex, surface: hex })
export const FundoConfig = z.object({
  paleta: z.enum(['padrao', 'conforto', 'personalizada']),
  claro: z.optional(CoresFundo),
  escuro: z.optional(CoresFundo),
})
export type FundoConfig = z.infer<typeof FundoConfig>
export const Configuracao = z.object({
  marca: Marca,
  fundo: FundoConfig,
  tema: TemaConfig,
  /** a pele da área do aluno, escolhida pela instituição */
  modoAluno: ModoAluno,
  suporteUrl: z.nullable(z.string()),
  /** como a plataforma chama aluno, curso, aula e os outros termos (docs/17) */
  glossario: Glossario,
  /** a tela de login e o que a área logada mostra (Ambiente, docs/22) */
  login: TelaDeLogin,
  areaLogada: AreaLogada,
})
export type Configuracao = z.infer<typeof Configuracao>
/** A configuração para quem configura: nome, endereço e tudo por assunto. */
export const ConfiguracaoPainel = z.object({ nome: z.string(), endereco: z.string(), configuracao: Configuracao })
export type ConfiguracaoPainel = z.infer<typeof ConfiguracaoPainel>
/** Salvamento automático por campo: só o que mudou; os grupos se mesclam no servidor. */
export const SalvarConfiguracao = z.object({
  nome: z.optional(z.string().check(z.trim(), z.minLength(2), z.maxLength(120))),
  configuracao: z.optional(
    z.object({
      marca: z.optional(
        z.object({
          nome: z.optional(z.string().check(z.maxLength(120))),
          corPrimaria: z.optional(hex),
          corSobrePrimaria: z.optional(hex),
          corDestaque: z.optional(z.nullable(hex)),
          corOverlay: z.optional(z.nullable(hex)),
          logoTamanho: z.optional(z.enum(['normal', 'reduzido'])),
          logoPosicao: z.optional(z.enum(['esquerda', 'centro'])),
        }),
      ),
      tema: z.optional(z.partial(TemaConfig)),
      fundo: z.optional(z.partial(FundoConfig)),
      modoAluno: z.optional(ModoAluno),
      glossario: z.optional(z.partial(Glossario)),
      login: z.optional(SalvarTelaDeLogin),
      areaLogada: z.optional(z.partial(AreaLogada)),
      suporteUrl: z.optional(
        z.nullable(
          z
            .string()
            .check(
              z.maxLength(400),
              z.regex(/^(https?:\/\/|mailto:)/, 'use um endereço completo (https://…) ou mailto:'),
            ),
        ),
      ),
    }),
  ),
})

/** O que todo mundo vê da instância (a casca, a entrada e as páginas públicas). */
export const Instancia = z.object({
  slug: z.string(),
  nome: z.string(),
  marca: z.object({
    nome: z.string(),
    cor: z.string(),
    corSobre: z.string(),
    corDestaque: z.nullable(z.string()),
    corOverlay: z.nullable(z.string()),
    logoUrl: z.nullable(z.string()),
    /**
     * o logo para fundo claro: o do topo da vitrine, quando há. A área logada o usa no tema claro e o da marca no
     * escuro (a marca pode ser a versão branca, feita para a tela de login e o tema escuro)
     */
    logoClaroUrl: z.optional(z.nullable(z.string())),
    faviconUrl: z.optional(z.nullable(z.string())),
    iconeUrl: z.optional(z.nullable(z.string())),
    logoTamanho: z.optional(z.enum(['normal', 'reduzido'])),
    logoPosicao: z.optional(z.enum(['esquerda', 'centro'])),
  }),
  modoAluno: ModoAluno,
  tema: TemaConfig,
  fundo: FundoConfig,
  suporteUrl: z.nullable(z.string()),
  glossario: Glossario,
  /** o módulo Ao vivo está contratado e ligado nesta instância (docs/16) */
  aoVivo: z.boolean(),
  /** as Conversas (o atendimento na Operação) estão ligadas nesta instância */
  conversas: z.boolean(),
  /** os Sites conectados estão ligados nesta instância (docs/50) */
  sites: z.optional(z.boolean()),
  /** o modo de operação e o que ele acende (docs/25) */
  modo: Modo,
  capacidades: Capacidades,
  /** a tela de login e o que a área logada mostra (docs/22) */
  login: TelaDeLogin,
  areaLogada: AreaLogada,
  /** a vitrine pública está no ar (o menu da conta leva até ela) */
  vitrine: z.optional(z.boolean()),
})
export type Instancia = z.infer<typeof Instancia>

// ---------------------------------------------------------------- permissões
// O catálogo cresce junto com os módulos: cada módulo portado traz as suas.
// Criar e editar perfis não é permissão: é só do superadmin (PO, 14/10/2026, docs/05).
export const PERMISSOES = [
  'painel.entrar',
  'colaboradores.ver',
  'colaboradores.editar',
  'instancia.configurar',
  'alunos.ver',
  'alunos.editar',
  'biblioteca.ver',
  'biblioteca.editar',
  'produtos.ver',
  'produtos.editar',
  'vitrine.editar',
  'comercial.ver',
  'comercial.editar',
  'rede.editar',
  'operacao.entrar',
  'matriculas.editar',
  'vendas.editar',
  'certificados.emitir',
  'duvidas.responder',
  'crm.operar',
  'balcao.pegar',
  'inscricao.criar',
  'atividades.corrigir',
  'aovivo.gerir',
  'financeiro.ver',
  'financeiro.operar',
  'saques.aprovar',
  'avisos.publicar',
  'treinamento.gerir',
  'treinamento.equipe',
  'secretaria.ver',
  'secretaria.editar',
  'secretaria.assinar',
] as const
export const Permissao = z.enum(PERMISSOES)
export type Permissao = z.infer<typeof Permissao>

export const GRUPOS_DE_PERMISSAO: { titulo: string; itens: { chave: Permissao; rotulo: string }[] }[] = [
  {
    titulo: 'Ambiente',
    itens: [
      { chave: 'painel.entrar', rotulo: 'Entrar no Ambiente' },
      {
        chave: 'instancia.configurar',
        rotulo: 'Personalizar o Ambiente: marca, vitrine, login, domínio, e-mails, campos e cobrança padrão',
      },
    ],
  },
  {
    titulo: 'Alunos',
    itens: [
      { chave: 'alunos.ver', rotulo: 'Ver alunos' },
      { chave: 'alunos.editar', rotulo: 'Cadastrar, editar, importar e convidar alunos' },
    ],
  },
  {
    titulo: 'Produtos, trilhas e combos',
    itens: [
      { chave: 'produtos.ver', rotulo: 'Ver produtos, trilhas e combos' },
      { chave: 'produtos.editar', rotulo: 'Criar e montar produtos, trilhas e combos' },
    ],
  },
  {
    titulo: 'Vitrine',
    itens: [{ chave: 'vitrine.editar', rotulo: 'Montar a vitrine e a página de curso' }],
  },
  {
    titulo: 'Comercial',
    itens: [
      { chave: 'comercial.ver', rotulo: 'Ver as ofertas (no Ambiente) e entrar no painel Comercial' },
      { chave: 'comercial.editar', rotulo: 'Criar e editar ofertas, cupons, vouchers, páginas e campanhas' },
      { chave: 'rede.editar', rotulo: 'Montar a rede comercial e a remuneração' },
    ],
  },
  {
    titulo: 'Operação',
    itens: [
      { chave: 'operacao.entrar', rotulo: 'Entrar no painel de Operação' },
      { chave: 'matriculas.editar', rotulo: 'Liberar e retirar acessos, montar turmas e a equipe dos cursos' },
      { chave: 'vendas.editar', rotulo: 'Confirmar, cancelar e estornar vendas' },
      { chave: 'certificados.emitir', rotulo: 'Emitir e revogar certificados' },
      { chave: 'duvidas.responder', rotulo: 'Responder as dúvidas dos alunos' },
      { chave: 'atividades.corrigir', rotulo: 'Corrigir as atividades enviadas pelos alunos' },
      { chave: 'crm.operar', rotulo: 'Operar as oportunidades (CRM)' },
      { chave: 'avisos.publicar', rotulo: 'Publicar avisos na área do aluno' },
      { chave: 'balcao.pegar', rotulo: 'Pegar e devolver leads no Balcão' },
      {
        chave: 'inscricao.criar',
        rotulo: 'Cadastrar e matricular alunos (Nova matrícula); quem opera um parceiro da rede, só os alunos dele',
      },
      {
        chave: 'aovivo.gerir',
        rotulo: 'Agendar e conduzir os encontros ao vivo (webinários, reuniões e links externos)',
      },
    ],
  },
  {
    titulo: 'Financeiro',
    itens: [
      { chave: 'financeiro.ver', rotulo: 'Ver o financeiro: cobranças, recebido, repasses e carteiras' },
      { chave: 'financeiro.operar', rotulo: 'Configurar o financeiro, os planos e as contas de recebimento' },
      { chave: 'saques.aprovar', rotulo: 'Aprovar, recusar e pagar os saques da rede' },
    ],
  },
  {
    titulo: 'Treinamento',
    itens: [
      {
        chave: 'treinamento.gerir',
        rotulo: 'Gerir o treinamento: grupos, formações obrigatórias, inscrições e relatórios de todos',
      },
      { chave: 'treinamento.equipe', rotulo: 'Acompanhar e aprovar inscrições só dos grupos que gere' },
    ],
  },
  {
    titulo: 'Secretaria',
    itens: [
      { chave: 'secretaria.ver', rotulo: 'Ver a Secretaria: matrizes, matrículas e documentos acadêmicos' },
      {
        chave: 'secretaria.editar',
        rotulo: 'Matricular formalmente, lançar o registro e emitir documentos acadêmicos',
      },
      { chave: 'secretaria.assinar', rotulo: 'Assinar os documentos acadêmicos, um a um ou em lote (o responsável)' },
    ],
  },
  {
    titulo: 'Biblioteca',
    itens: [
      { chave: 'biblioteca.ver', rotulo: 'Ver o acervo' },
      { chave: 'biblioteca.editar', rotulo: 'Enviar, organizar e apagar itens do acervo' },
    ],
  },
  {
    titulo: 'Colaboradores',
    itens: [
      { chave: 'colaboradores.ver', rotulo: 'Ver a equipe' },
      { chave: 'colaboradores.editar', rotulo: 'Cadastrar, editar, convidar e desativar colaboradores' },
    ],
  },
]

// ---------------------------------------------------------------- sessão e entrada
const email = z.string().check(z.trim(), z.toLowerCase(), z.email('e-mail inválido'))

export const Sessao = z.object({
  pessoa: z.object({ id: z.nullable(z.string()), nome: z.string(), email: z.string() }),
  /** a attiwork: vê e faz tudo em qualquer instância, nunca aparece na equipe */
  superadmin: z.boolean(),
  /** os papéis da pessoa na instância (o superadmin não tem) */
  papeis: z.array(z.enum(['aluno', 'colaborador'])),
  perfil: z.nullable(z.object({ id: z.string(), nome: z.string() })),
  permissoes: z.array(Permissao),
})
export type Sessao = z.infer<typeof Sessao>

export const PedirCodigo = z.object({ email })
export const PedidoDeCodigo = z.object({
  enviado: z.literal(true),
  /** só no modo de desenvolvimento local, para entrar sem e-mail */
  codigoDev: z.optional(z.string()),
})
/** O código de 6 dígitos; para o superadmin, também a senha mestre (o coringa, 6 dígitos). */
export const ConfirmarCodigo = z.object({ email, codigo: z.string().check(z.regex(/^\d{6}$/, 'código de 6 dígitos')) })

// ---------------------------------------------------------------- superadmin (docs/05)
/** A ficha da instância, só leitura: o que o suporte confere primeiro. */
export const FichaInstancia = z.object({
  nome: z.string(),
  slug: z.string(),
  endereco: z.string(),
  versao: z.string(),
  colaboradores: z.int(),
  /** aluno ativo = tem ao menos um acesso vigente (não de teste, não vencido) */
  alunos: z.int(),
  /** todos os alunos cadastrados, com ou sem acesso */
  alunosCadastrados: z.int(),
  perfis: z.int(),
  camposPersonalizados: z.int(),
})
export type FichaInstancia = z.infer<typeof FichaInstancia>

// ---------------------------------------------------------------- perfis e colaboradores
/** O perfil 360 do colaborador (PO, 19/10/2026): o que ele pode, o que conduz, onde está na rede e o que tem em mãos. */
export const Colaborador360 = z.object({
  permissoes: z.array(z.string()),
  /** a equipe dos cursos: papel e a que ele se liga */
  equipe: z.array(
    z.object({ papel: z.string(), escopo: z.enum(['plataforma', 'modalidade', 'curso', 'turma']), alvo: z.string() }),
  ),
  /** os parceiros da rede de que a pessoa faz parte */
  parceiros: z.array(z.object({ id: z.string(), nome: z.string(), tipo: z.string() })),
  oportunidades: z.object({ abertas: z.int(), ganhas: z.int(), perdidas: z.int() }),
  ultimaEntrada: z.nullable(z.string()),
  desde: z.nullable(z.string()),
})
export type Colaborador360 = z.infer<typeof Colaborador360>

export const Perfil = z.object({
  id: z.string(),
  chave: z.nullable(z.string()),
  nome: z.string(),
  permissoes: z.array(Permissao),
  sistema: z.boolean(),
  /** Administrador: sempre com tudo, para a instância nunca ficar sem quem a configure */
  travado: z.boolean(),
  pessoas: z.int(),
})
export type Perfil = z.infer<typeof Perfil>

export const SalvarPerfil = z.object({
  nome: z.string().check(z.trim(), z.minLength(2, 'nome curto demais'), z.maxLength(60)),
  permissoes: z.array(Permissao),
})

export const Colaborador = z.object({
  id: z.string(),
  nome: z.string(),
  email: z.string(),
  /** o código da pessoa no sistema de gestão, como o RA */
  idExterno: z.nullable(z.string()),
  telefone: z.nullable(z.string()),
  ativo: z.boolean(),
  perfil: z.object({ id: z.string(), nome: z.string() }),
  desde: z.string(),
})
export type Colaborador = z.infer<typeof Colaborador>

export const ListaColaboradores = z.object({
  total: z.int(),
  pagina: z.int(),
  porPagina: z.int(),
  itens: z.array(Colaborador),
})

const textoOpcional = z.optional(z.nullable(z.string().check(z.trim(), z.maxLength(60))))
export const NovoColaborador = z.object({
  nome: z.string().check(z.trim(), z.minLength(2, 'nome curto demais'), z.maxLength(120)),
  email,
  perfilId: z.string(),
  idExterno: textoOpcional,
  telefone: textoOpcional,
  ativo: z.optional(z.boolean()),
})
export const EditarColaborador = z.object({
  nome: z.optional(z.string().check(z.trim(), z.minLength(2, 'nome curto demais'), z.maxLength(120))),
  perfilId: z.optional(z.string()),
  idExterno: textoOpcional,
  telefone: textoOpcional,
  ativo: z.optional(z.boolean()),
})
/** Convite: enviado por e-mail; sem envio configurado, o modo local devolve o link. */
export const Convite = z.object({
  enviado: z.boolean(),
  para: z.string(),
  expiraEm: z.string(),
  linkDev: z.optional(z.string()),
})

const CursoCartao = z.object({
  id: z.string(),
  nome: z.string(),
  aulas: z.int(),
  aulasConcluidas: z.int(),
  cargaHoraria: z.nullable(z.int()),
  selo: z.nullable(z.string()),
  /** a capa vertical (pôster) ou, sem ela, a larga */
  capa: z.optional(z.nullable(z.string())),
})
export type CursoCartao = z.infer<typeof CursoCartao>

const Notificacao = z.object({
  id: z.string(),
  tipo: z.enum(['resposta', 'ao-vivo', 'certificado', 'aula', 'encontro']),
  titulo: z.string(),
  texto: z.string(),
  em: z.iso.datetime(),
  lida: z.boolean(),
  /** para onde o aviso leva (ex.: a sala do encontro ao vivo) */
  para: z.optional(z.string()),
})
export type Notificacao = z.infer<typeof Notificacao>

export const InicioAluno = z.object({
  cursos: z.array(CursoCartao),
  notificacoes: z.array(Notificacao),
  concluidos: z.array(CursoCartao),
})
export type InicioAluno = z.infer<typeof InicioAluno>

export const Erro = z.object({ erro: z.string() })

// ---------------------------------------------------------------- alunos (porte de aw-ava/contrato/aluno.ts e conta.ts)

const linhaAluno = {
  id: z.string(),
  nome: z.string(),
  email: z.string(),
  idExterno: z.nullable(z.string()),
  telefone: z.nullable(z.string()),
  ativo: z.boolean(),
  desde: z.string(),
}
export const AlunoResumo = z.object(linhaAluno)
export type AlunoResumo = z.infer<typeof AlunoResumo>
export const ListaAlunos = z.object({
  total: z.int(),
  pagina: z.int(),
  porPagina: z.int(),
  itens: z.array(AlunoResumo),
})

const textoLivre = z.optional(z.string())
export const Endereco = z.object({
  cep: textoLivre,
  logradouro: textoLivre,
  numero: textoLivre,
  complemento: textoLivre,
  bairro: textoLivre,
  cidade: textoLivre,
  uf: textoLivre,
})
export const Sobre = z.object({
  apelido: textoLivre,
  bio: textoLivre,
  linkedin: textoLivre,
  instagram: textoLivre,
  profissao: textoLivre,
})
export const FichaAluno = z.object({
  ...linhaAluno,
  /** também é da equipe (aparece em Colaboradores) */
  colaborador: z.boolean(),
  perfil: z.object({
    cpf: z.nullable(z.string()),
    nascimento: z.nullable(z.string()),
    endereco: Endereco,
    sobre: Sobre,
  }),
})
export type FichaAluno = z.infer<typeof FichaAluno>

/** CPF válido pelos dígitos verificadores (aceita com ou sem máscara). */
export const cpfValido = (v: string) => {
  const d = v.replace(/\D/g, '')
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false
  for (let t = 9; t < 11; t++) {
    let s = 0
    for (let i = 0; i < t; i++) s += Number(d[i]) * (t + 1 - i)
    if (((s * 10) % 11) % 10 !== Number(d[t])) return false
  }
  return true
}

// ---------------------------------------------------------------- Portal do aluno: o próprio perfil (porte de aw-ava/contrato/conta.ts)
export const CampoTravado = z.enum(['nome', 'cpf', 'nascimento'])
/** O perfil que o aluno mantém. Nome, CPF e nascimento travam quando já há certificado emitido com eles. */
export const PerfilDoAluno = z.object({
  nome: z.string(),
  email: z.string(),
  telefone: z.nullable(z.string()),
  cpf: z.nullable(z.string()),
  nascimento: z.nullable(z.string()),
  endereco: Endereco,
  sobre: Sobre,
  travados: z.array(CampoTravado),
  motivoTrava: z.nullable(z.enum(['certificado'])),
})
export type PerfilDoAluno = z.infer<typeof PerfilDoAluno>

const ate = (n: number) => z.optional(z.string().check(z.trim(), z.maxLength(n)))
/** Data real no formato AAAA-MM-DD, entre 1900 e hoje. */
const dataDeNascimento = (v: string) => {
  const d = new Date(`${v}T12:00:00Z`)
  return (
    !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v && d.getUTCFullYear() >= 1900 && d <= new Date()
  )
}
export const EditarPerfilDoAluno = z.object({
  nome: z.optional(
    z.string().check(
      z.trim(),
      z.minLength(2, 'nome curto demais'),
      z.maxLength(160),
      z.refine((v) => v.split(/\s+/).length >= 2, 'informe nome e sobrenome'),
    ),
  ),
  telefone: z.optional(z.nullable(z.string().check(z.trim(), z.maxLength(30)))),
  /** só os 11 dígitos; vazio apaga */
  cpf: z.optional(
    z.nullable(
      z.string().check(
        z.trim(),
        z.regex(/^(\d{11})?$/, 'CPF com 11 dígitos, só números'),
        z.refine((v) => !v || cpfValido(v), 'CPF inválido'),
      ),
    ),
  ),
  /** AAAA-MM-DD; vazio apaga */
  nascimento: z.optional(
    z.nullable(
      z.string().check(
        z.trim(),
        z.regex(/^(\d{4}-\d{2}-\d{2})?$/, 'data de nascimento no formato AAAA-MM-DD'),
        z.refine((v) => !v || dataDeNascimento(v), 'data de nascimento fora do esperado'),
      ),
    ),
  ),
  endereco: z.optional(
    z.object({
      cep: z.optional(z.string().check(z.trim(), z.regex(/^(\d{8})?$/, 'CEP com 8 dígitos, só números'))),
      logradouro: ate(160),
      numero: ate(20),
      complemento: ate(80),
      bairro: ate(80),
      cidade: ate(80),
      uf: ate(2),
    }),
  ),
  sobre: z.optional(
    z.object({
      apelido: ate(40),
      bio: ate(400),
      linkedin: ate(200),
      instagram: ate(60),
      profissao: ate(80),
    }),
  ),
})

/** Os certificados do aluno no Portal: os emitidos (vigentes) e os cursos prontos para emitir. */
export const MeusCertificados = z.object({
  emitidos: z.array(
    z.object({
      codigo: z.string(),
      cursoId: z.string(),
      curso: z.string(),
      emitidoEm: z.string(),
      capa: z.nullable(z.string()),
      /** certificado formal: o lugar no livro de registro */
      registro: z.optional(z.object({ numero: z.int(), livro: z.int(), folha: z.int() })),
    }),
  ),
  prontos: z.array(z.object({ cursoId: z.string(), curso: z.string() })),
})
export type MeusCertificados = z.infer<typeof MeusCertificados>

/** Sugestões de profissão no perfil; a pessoa pode escrever a sua. */
export const PROFISSOES_SUGERIDAS = [
  'Administrador(a)',
  'Advogado(a)',
  'Agente comunitário(a) de saúde',
  'Analista de sistemas',
  'Arquiteto(a)',
  'Assistente administrativo(a)',
  'Assistente social',
  'Autônomo(a)',
  'Auxiliar de enfermagem',
  'Bancário(a)',
  'Biólogo(a)',
  'Comerciante',
  'Consultor(a)',
  'Contador(a)',
  'Coordenador(a) pedagógico(a)',
  'Corretor(a) de imóveis',
  'Cuidador(a)',
  'Dentista',
  'Designer',
  'Desenvolvedor(a) de software',
  'Educador(a) físico(a)',
  'Empresário(a)',
  'Enfermeiro(a)',
  'Engenheiro(a)',
  'Estudante',
  'Esteticista',
  'Farmacêutico(a)',
  'Fisioterapeuta',
  'Fonoaudiólogo(a)',
  'Funcionário(a) público(a)',
  'Gerente',
  'Gestor(a) escolar',
  'Jornalista',
  'Médico(a)',
  'Militar',
  'Motorista',
  'Nutricionista',
  'Pedagogo(a)',
  'Policial',
  'Professor(a)',
  'Psicólogo(a)',
  'Publicitário(a)',
  'Recepcionista',
  'Recursos humanos',
  'Secretário(a)',
  'Técnico(a) de enfermagem',
  'Técnico(a) em segurança do trabalho',
  'Terapeuta ocupacional',
  'Vendedor(a)',
  'Veterinário(a)',
] as const

const opcional60 = z.optional(z.nullable(z.string().check(z.trim(), z.maxLength(60))))
export const NovoAluno = z.object({
  nome: z.string().check(z.trim(), z.minLength(2, 'nome curto demais'), z.maxLength(160)),
  email,
  idExterno: opcional60,
  telefone: opcional60,
  ativo: z.optional(z.boolean()),
})
export const EditarAluno = z.object({
  nome: z.optional(z.string().check(z.trim(), z.minLength(2, 'nome curto demais'), z.maxLength(160))),
  idExterno: opcional60,
  telefone: opcional60,
  ativo: z.optional(z.boolean()),
})
export const ImportarAlunos = z.object({
  linhas: z
    .array(
      z.object({
        nome: z.string(),
        email: z.string(),
        idExterno: z.optional(z.string()),
        telefone: z.optional(z.string()),
      }),
    )
    .check(z.maxLength(5000, 'no máximo 5.000 linhas por vez')),
})
export const ResultadoImportacao = z.object({
  criadas: z.int(),
  atualizadas: z.int(),
  erros: z.array(z.object({ linha: z.int(), erro: z.string() })),
})

// ---------------------------------------------------------------- campos personalizados (porte de aw-ava/contrato/atributos.ts)
export const TipoAtributo = z.enum(['texto', 'numero', 'data', 'lista', 'simnao'])
export type TipoAtributo = z.infer<typeof TipoAtributo>
export const VisibilidadeAtributo = z.enum(['oculto', 'aluno_ve', 'aluno_edita'])
/** A que o campo se liga (PO, 19/10/2026): o aluno, a matrícula, o parceiro (organização da rede) ou o colaborador. */
export const NivelAtributo = z.enum(['aluno', 'matricula', 'parceiro', 'colaborador'])
export type NivelAtributo = z.infer<typeof NivelAtributo>
export const ROTULO_NIVEL: Record<NivelAtributo, string> = {
  aluno: 'Aluno',
  matricula: 'Matrícula',
  parceiro: 'Parceiro',
  colaborador: 'Colaborador',
}
export const PedirNoCheckout = z.enum(['nao', 'opcional', 'obrigatorio', 'oculto'])
export type PedirNoCheckout = z.infer<typeof PedirNoCheckout>
export const PedirNaInscricao = z.enum(['nao', 'opcional', 'obrigatorio'])
export type PedirNaInscricao = z.infer<typeof PedirNaInscricao>
export const Atributo = z.object({
  id: z.string(),
  nivel: NivelAtributo,
  chave: z.string(),
  rotulo: z.string(),
  tipo: TipoAtributo,
  opcoes: z.array(z.string()),
  visibilidade: VisibilidadeAtributo,
  ordem: z.number(),
  origem: z.enum(['painel', 'api']),
  /** pedir no checkout (PO, 21/10/2026): não, opcional ou obrigatório; só nos campos do aluno */
  checkout: PedirNoCheckout,
  /** pedir na ficha de inscrição (PO, 22/10/2026): não, opcional ou obrigatório; só nos campos do aluno */
  inscricao: PedirNaInscricao,
  criadoEm: z.string(),
  /** quantos alunos têm o campo preenchido (o aviso antes de apagar) */
  preenchidos: z.int(),
})
export type Atributo = z.infer<typeof Atributo>
const chaveAtributo = z
  .string()
  .check(
    z.trim(),
    z.toLowerCase(),
    z.regex(/^[a-z][a-z0-9_]{0,39}$/, 'chave: letras minúsculas, números e _ (começa com letra)'),
  )
export const AtributoEntrada = z.object({
  nivel: z.optional(NivelAtributo),
  rotulo: z.string().check(z.trim(), z.minLength(1), z.maxLength(60)),
  chave: z.optional(chaveAtributo),
  tipo: z.optional(TipoAtributo),
  opcoes: z.optional(z.array(z.string().check(z.trim(), z.minLength(1), z.maxLength(80))).check(z.maxLength(100))),
  visibilidade: z.optional(VisibilidadeAtributo),
  checkout: z.optional(PedirNoCheckout),
  inscricao: z.optional(PedirNaInscricao),
})
export const AtributoEdicao = z.object({
  rotulo: z.optional(z.string().check(z.trim(), z.minLength(1), z.maxLength(60))),
  tipo: z.optional(TipoAtributo),
  opcoes: z.optional(z.array(z.string().check(z.trim(), z.minLength(1), z.maxLength(80))).check(z.maxLength(100))),
  visibilidade: z.optional(VisibilidadeAtributo),
  checkout: z.optional(PedirNoCheckout),
  inscricao: z.optional(PedirNaInscricao),
})
/** A ordem dos campos, que é a ordem na ficha do aluno: todos os ids, na ordem nova. */
export const OrdemAtributos = z.object({ ids: z.array(z.string()).check(z.maxLength(500)) })
/** Valores: chave → valor (texto, número, data AAAA-MM-DD, opção da lista, sim/não). `null` apaga. */
export const ValoresAtributos = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
export type ValoresAtributos = z.infer<typeof ValoresAtributos>

// ---------------------------------------------------------------- biblioteca (docs/06)
/** As gavetas são a finalidade do item, escolhida ao enviar. Os modelos de certificado têm cadastro próprio. */
export const GAVETAS = ['conteudos', 'materiais', 'entregaveis', 'imagens'] as const
export const Gaveta = z.enum(GAVETAS)
export type Gaveta = z.infer<typeof Gaveta>
export const TipoItem = z.enum(['video_externo', 'video_arquivo', 'html', 'embed', 'pdf', 'arquivo', 'imagem'])
export type TipoItem = z.infer<typeof TipoItem>
/** O que cada gaveta aceita. Entregável é destacado: nunca aparece como material de aula (PO). */
export const TIPOS_DA_GAVETA: Record<Gaveta, readonly TipoItem[]> = {
  conteudos: ['video_externo', 'video_arquivo', 'html', 'embed', 'pdf'],
  materiais: ['pdf', 'arquivo', 'imagem'],
  entregaveis: ['pdf', 'arquivo'],
  imagens: ['imagem'],
}
/** Até 100 MB por envio: é o limite do pedido no Workers. Vídeo maior vai por link. */
export const TAMANHO_MAXIMO = 100 * 1024 * 1024

export const ItemBiblioteca = z.object({
  id: z.string(),
  gaveta: Gaveta,
  tipo: TipoItem,
  nome: z.string(),
  /** link externo, ou o endereço de leitura do arquivo enviado */
  url: z.string(),
  /** arquivo enviado (não link) */
  arquivo: z.boolean(),
  mime: z.nullable(z.string()),
  tamanho: z.nullable(z.number()),
  etiquetas: z.array(z.string()),
  criadoEm: z.string(),
})
export type ItemBiblioteca = z.infer<typeof ItemBiblioteca>
export const ListaItens = z.object({
  total: z.int(),
  pagina: z.int(),
  porPagina: z.int(),
  itens: z.array(ItemBiblioteca),
})
/** O resumo da gaveta: quantos itens e as etiquetas usadas (as pastas saíram, PO 14/10). */
export const ResumoDaGaveta = z.object({
  total: z.int(),
  etiquetas: z.array(z.object({ etiqueta: z.string(), itens: z.int() })),
})
export type ResumoDaGaveta = z.infer<typeof ResumoDaGaveta>

const nomeItem = z.string().check(z.trim(), z.minLength(1, 'dê um nome'), z.maxLength(200))
const etiqueta = z.string().check(z.trim(), z.minLength(1), z.maxLength(40))
const etiquetas = z.array(etiqueta).check(z.maxLength(20, 'no máximo 20 etiquetas'))
const urlExterna = z
  .string()
  .check(z.trim(), z.maxLength(2000), z.regex(/^https:\/\/\S+$/, 'use um endereço https:// completo'))
export const NovoLink = z.object({
  tipo: z.enum(['video_externo', 'embed']),
  nome: z.optional(z.string().check(z.trim(), z.maxLength(200))),
  url: urlExterna,
  etiquetas: z.optional(etiquetas),
})
export const EditarItem = z.object({
  nome: z.optional(nomeItem),
  url: z.optional(urlExterna),
  etiquetas: z.optional(etiquetas),
})

// ---------------------------------------------------------------- modelos de certificado (docs/06, fase 2)
// Porte de aw-ava/contrato/certificado.ts. Mudou: no AVA só existe o certificado (os
// documentos formais são da torre), as variáveis formais saíram (matrícula, registro,
// livro, folha, credenciamento) e a arte de fundo é do próprio modelo, não da gaveta
// Imagens. A origem diz se o modelo é próprio ou veio da certificadora (só leitura).

/** Variáveis que os textos do modelo aceitam. */
export const VARIAVEIS_CERTIFICADO = [
  { chave: 'nome', rotulo: 'Nome do aluno', exemplo: 'Maria Aparecida da Silva' },
  { chave: 'cpf', rotulo: 'CPF', exemplo: '529.982.247-25' },
  { chave: 'curso', rotulo: 'Curso', exemplo: 'Gestão Escolar e Liderança' },
  { chave: 'carga', rotulo: 'Carga horária', exemplo: '360 horas' },
  { chave: 'inicio', rotulo: 'Data de início', exemplo: '10 de março de 2026' },
  { chave: 'conclusao', rotulo: 'Data de conclusão', exemplo: '20 de agosto de 2026' },
  { chave: 'data', rotulo: 'Data de certificação', exemplo: '20 de agosto de 2026' },
  { chave: 'instituicao', rotulo: 'Instituição', exemplo: 'Instituto Horizonte' },
  { chave: 'codigo', rotulo: 'Código de validação', exemplo: 'ABCDE-FGH23' },
  { chave: 'validacao', rotulo: 'Link de validação', exemplo: 'horizonte.aulastack.app/validar/ABCDE-FGH23' },
  { chave: 'matricula', rotulo: 'Número da matrícula', exemplo: '000123' },
  { chave: 'registro', rotulo: 'Registro no livro', exemplo: '482' },
  { chave: 'livro', rotulo: 'Livro de registro', exemplo: '3' },
  { chave: 'folha', rotulo: 'Folha do livro', exemplo: '82' },
  { chave: 'assinante', rotulo: 'Nome de quem assina', exemplo: 'Helena Prado Queiroz' },
  { chave: 'cargo', rotulo: 'Cargo de quem assina', exemplo: 'Secretária acadêmica' },
] as const
export type VariavelCertificado = (typeof VARIAVEIS_CERTIFICADO)[number]['chave']

export const COLUNAS_HISTORICO = [
  { chave: 'disciplina', rotulo: 'Disciplina' },
  { chave: 'carga', rotulo: 'Carga' },
  { chave: 'nota', rotulo: 'Nota' },
  { chave: 'situacao', rotulo: 'Situação' },
  { chave: 'docente', rotulo: 'Docente' },
  { chave: 'periodo', rotulo: 'Período' },
] as const
export const ColunaHistorico = z.enum(['disciplina', 'carga', 'nota', 'situacao', 'docente', 'periodo'])
export type ColunaHistorico = z.infer<typeof ColunaHistorico>

const corHex = z.string().check(z.regex(/^#[0-9a-fA-F]{6}$/))
export const EstiloElemento = z.object({
  /** tamanho da fonte em pt (relativo a uma página A4) */
  tamanho: z.number().check(z.gte(5), z.lte(96)),
  peso: z.enum(['normal', 'bold']),
  italico: z.boolean(),
  alinhamento: z.enum(['left', 'center', 'right']),
  fonte: z.enum(['sans', 'serif', 'formal', 'gotica']),
  cor: corHex,
})
export type EstiloElemento = z.infer<typeof EstiloElemento>
const pct = z.number().check(z.gte(0), z.lte(100))
export const ElementoCertificado = z.object({
  id: z.string().check(z.minLength(1), z.maxLength(40)),
  pagina: z.enum(['frente', 'verso']),
  tipo: z.enum(['texto', 'tabela', 'assinatura', 'linha']),
  /** posição e tamanho em % da página */
  x: pct,
  y: pct,
  w: z.number().check(z.gte(1), z.lte(100)),
  h: z.number().check(z.gte(0.2), z.lte(100)),
  /** texto com variáveis ({nome}, {curso}…); na assinatura, a 1ª linha é o nome e a 2ª o cargo */
  texto: z.string().check(z.maxLength(4000)),
  colunas: z.array(ColunaHistorico),
  estilo: EstiloElemento,
})
export type ElementoCertificado = z.infer<typeof ElementoCertificado>
const arquivoArte = z.nullable(z.string().check(z.regex(/^arte-[0-9a-f-]{36}\.(png|jpg|webp)$/)))
export const LayoutCertificado = z.object({
  orientacao: z.enum(['paisagem', 'retrato']),
  /** o selo de autenticidade (QR, marca da instituição e dados de validação) no verso; ausente = ligado */
  selo: z.optional(z.boolean()),
  /** a arte de fundo de cada página (arquivo do próprio modelo) */
  fundoFrente: arquivoArte,
  fundoVerso: arquivoArte,
  elementos: z.array(ElementoCertificado).check(z.maxLength(80)),
})
export type LayoutCertificado = z.infer<typeof LayoutCertificado>

export const ModeloCertificado = z.object({
  id: z.string(),
  nome: z.string(),
  descricao: z.nullable(z.string()),
  origem: z.enum(['propria', 'certificadora']),
  /** só nos da certificadora: de quem veio (aqui é só leitura) */
  certificadora: z.nullable(z.string()),
  layout: LayoutCertificado,
  fundoFrenteUrl: z.nullable(z.string()),
  fundoVersoUrl: z.nullable(z.string()),
  atualizadoEm: z.string(),
})
export type ModeloCertificado = z.infer<typeof ModeloCertificado>
export const NovoModelo = z.object({ nome: z.string().check(z.trim(), z.minLength(2), z.maxLength(120)) })
export const SalvarModelo = z.object({
  nome: z.string().check(z.trim(), z.minLength(2, 'nome curto demais'), z.maxLength(120)),
  descricao: z.optional(z.nullable(z.string().check(z.trim(), z.maxLength(400)))),
  layout: LayoutCertificado,
})

/** Preenche as variáveis de um texto do modelo. Variável desconhecida fica como está. */
export function preencherVariaveis(texto: string, valores: Partial<Record<VariavelCertificado, string>>) {
  return texto.replace(/\{(\w+)\}/g, (m, k: string) => (k in valores ? (valores[k as VariavelCertificado] ?? '') : m))
}

/** Valores de exemplo (prévia do construtor). */
export const EXEMPLO_CERTIFICADO = Object.fromEntries(VARIAVEIS_CERTIFICADO.map((v) => [v.chave, v.exemplo])) as Record<
  VariavelCertificado,
  string
>

/**
 * As fontes do certificado. Formal (Cinzel, de diploma) e gótica (UnifrakturCook, de chancelaria) são
 * da SIL Open Font License 1.1: uso e redistribuição livres, inclusive embutidas no PDF (a impressão do
 * navegador as leva dentro do arquivo). Ficam em /fontes (apps/ava/public/fontes, com as licenças).
 */
export const FONTES_DO_CERTIFICADO: Record<EstiloElemento['fonte'], { rotulo: string; css: string }> = {
  sans: { rotulo: 'Sem serifa', css: 'inherit' },
  serif: { rotulo: 'Serifada', css: "Georgia, 'Times New Roman', serif" },
  formal: { rotulo: 'Formal (diploma)', css: "'Cinzel', Georgia, serif" },
  gotica: { rotulo: 'Gótica (chancelaria)', css: "'UnifrakturCook', 'Old English Text MT', Georgia, serif" },
}
export const CSS_DAS_FONTES_DO_CERTIFICADO = `@font-face{font-family:'Cinzel';font-weight:400;font-display:swap;src:url(/fontes/cinzel-latin-400-normal.woff2) format('woff2')}@font-face{font-family:'Cinzel';font-weight:700;font-display:swap;src:url(/fontes/cinzel-latin-700-normal.woff2) format('woff2')}@font-face{font-family:'UnifrakturCook';font-weight:700;font-display:swap;src:url(/fontes/unifrakturcook-latin-700-normal.woff2) format('woff2')}`

const ESTILO_PADRAO: EstiloElemento = {
  tamanho: 14,
  peso: 'normal',
  italico: false,
  alinhamento: 'left',
  fonte: 'sans',
  cor: '#131920',
}
/** Um elemento novo com os padrões; o construtor e o modelo inicial partem daqui. */
export const elemento = (
  e: Pick<ElementoCertificado, 'id' | 'tipo' | 'x' | 'y' | 'w' | 'h'> &
    Partial<Omit<ElementoCertificado, 'estilo'>> & { estilo?: Partial<EstiloElemento> },
): ElementoCertificado => ({
  pagina: 'frente',
  texto: '',
  colunas: ['disciplina', 'carga', 'nota', 'docente'],
  ...e,
  estilo: { ...ESTILO_PADRAO, ...e.estilo },
})

/** O modelo inicial: um certificado limpo, com o histórico no verso, para começar a arrastar. */
export function layoutInicial(): LayoutCertificado {
  return {
    orientacao: 'paisagem',
    fundoFrente: null,
    fundoVerso: null,
    elementos: [
      elemento({
        id: 'titulo',
        tipo: 'texto',
        x: 10,
        y: 14,
        w: 80,
        h: 10,
        texto: 'CERTIFICADO',
        estilo: { tamanho: 36, peso: 'bold', alinhamento: 'center', fonte: 'serif' },
      }),
      elemento({
        id: 'corpo',
        tipo: 'texto',
        x: 12,
        y: 32,
        w: 76,
        h: 22,
        texto:
          'Certificamos que {nome} concluiu o curso {curso}, com carga horária de {carga}, no período de {inicio} a {conclusao}.',
        estilo: { tamanho: 16, alinhamento: 'center', fonte: 'serif' },
      }),
      elemento({
        id: 'local',
        tipo: 'texto',
        x: 12,
        y: 58,
        w: 76,
        h: 6,
        texto: '{instituicao}, {data}.',
        estilo: { tamanho: 12, alinhamento: 'center' },
      }),
      elemento({
        id: 'assinatura',
        tipo: 'assinatura',
        x: 35,
        y: 70,
        w: 30,
        h: 10,
        texto: 'Nome de quem assina\nDireção',
        estilo: { tamanho: 11, alinhamento: 'center' },
      }),
      elemento({
        id: 'codigo',
        tipo: 'texto',
        x: 60,
        y: 90,
        w: 36,
        h: 6,
        texto: 'Código {codigo} · valide em {validacao}',
        estilo: { tamanho: 8, alinhamento: 'right', cor: '#5f5f67' },
      }),
      elemento({
        id: 'historico-titulo',
        pagina: 'verso',
        tipo: 'texto',
        x: 8,
        y: 6,
        w: 84,
        h: 6,
        texto: 'Histórico · {nome} · {curso}',
        estilo: { tamanho: 14, peso: 'bold' },
      }),
      elemento({ id: 'historico', pagina: 'verso', tipo: 'tabela', x: 8, y: 14, w: 84, h: 70, estilo: { tamanho: 9 } }),
    ],
  }
}

// ---------------------------------------------------------------- produtos (docs/08)
/**
 * Os tipos de produto, como no aw-ava (contrato/produto.ts). O id interno não muda; o
 * rótulo é o da produção. A formação é um tipo de produto (PO, 14/10/2026), com texto
 * neutro: sem graduação nem pós.
 */
export const TIPOS_PRODUTO = [
  {
    id: 'curso',
    rotulo: 'Curso',
    plural: 'Cursos',
    texto: 'Aulas organizadas em partes, com avaliações e certificado.',
  },
  {
    id: 'formacao',
    rotulo: 'Formação',
    plural: 'Formações',
    texto: 'Vários cursos com regra de conclusão e o certificado do conjunto.',
  },
  {
    id: 'ebook',
    rotulo: 'Entregável',
    plural: 'Entregáveis',
    texto: 'Arquivos para ler ou baixar: ebook, apostila, planilha, modelo.',
  },
  {
    id: 'evento',
    rotulo: 'Ao vivo',
    plural: 'Ao vivo',
    texto: 'Live com data e vagas, ligada ou não a um curso. O acesso é o ingresso.',
  },
  {
    id: 'mentoria',
    rotulo: 'Mentoria',
    plural: 'Mentorias',
    texto: 'Sessões individuais com um mentor, em pacote. Cada sessão é marcada na agenda.',
  },
  {
    id: 'aula_grupo',
    rotulo: 'Aula em grupo',
    plural: 'Aulas em grupo',
    texto: 'Turma pequena com encontros marcados e vagas, ao vivo.',
  },
  {
    id: 'biblioteca',
    rotulo: 'Conteúdo',
    plural: 'Conteúdos',
    texto: 'Vídeos, PDFs e outros conteúdos como acervo, para consumo livre, sem trilha nem certificado.',
  },
  {
    id: 'certificacao',
    rotulo: 'Certificação',
    plural: 'Certificações',
    texto: 'O direito ao certificado, vendido à parte; ou o certificado avulso, por prova ou por comprovação.',
  },
] as const
export const TipoProduto = z.enum([
  'curso',
  'formacao',
  'ebook',
  'evento',
  'mentoria',
  'aula_grupo',
  'biblioteca',
  'certificacao',
])
export type TipoProduto = z.infer<typeof TipoProduto>
/** Os tipos que já se constroem aqui: todos (docs/11). */
export const TIPOS_PRONTOS: readonly TipoProduto[] = [
  'curso',
  'formacao',
  'ebook',
  'evento',
  'mentoria',
  'aula_grupo',
  'biblioteca',
  'certificacao',
]
export const StatusProduto = z.enum(['rascunho', 'publicado', 'arquivado'])
export type StatusProduto = z.infer<typeof StatusProduto>

export const ProdutoResumo = z.object({
  id: z.string(),
  tipo: TipoProduto,
  titulo: z.string(),
  status: StatusProduto,
  modalidade: z.nullable(z.string()),
  /** o endereço público (`/<slug>`); nulo até ter um */
  slug: z.optional(z.nullable(z.string())),
  capaUrl: z.nullable(z.string()),
  atualizadoEm: z.string(),
})
export type ProdutoResumo = z.infer<typeof ProdutoResumo>
const tituloProduto = z.string().check(z.trim(), z.minLength(2, 'dê um título'), z.maxLength(200))
export const NovoProduto = z.object({ tipo: TipoProduto, titulo: tituloProduto })
/** As três capas, todas da gaveta Imagens: larga 16:9 (topo e cartões), 4:3 (cartão grande) e vertical 2:3 (pôster). */
export const FORMATOS_CAPA = ['larga', 'quadro', 'vertical'] as const
export type FormatoCapa = (typeof FORMATOS_CAPA)[number]
const capa = z.object({ id: z.nullable(z.string()), url: z.nullable(z.string()) })
/**
 * A certificação do produto (passo 4). O certificado sai da própria instituição, com um modelo próprio
 * (`quem: 'aqui'`); o valor 'certificadora' só existe para ler dado antigo, a tela e a rota não o aceitam
 * mais (docs/46, fatia 2). A regra de liberação é o portão: não decide nota.
 */
export const CertificacaoProduto = z.object({
  emite: z.boolean(),
  /** só na formação: certifica só pelo conjunto, ou os dois (cada curso o seu e a formação o dela) */
  pelo: z.optional(z.enum(['conjunto', 'ambos'])),
  quem: z.enum(['aqui', 'certificadora']),
  modeloId: z.nullable(z.string()),
  /** só leitura: dado antigo de um vínculo que não existe mais */
  certificadora: z.optional(z.nullable(z.string())),
  checklist: z.object({
    bloqueado: z.boolean(),
    /** as provas marcadas "trava o certificado" precisam estar concluídas */
    avaliacoes: z.boolean(),
    /** regra antiga: mínimo de aulas concluídas, em % das publicadas (só leitura quando há `minPartes`) */
    minAulasPct: z.int().check(z.gte(0), z.lte(100)),
    /**
     * regra atual: quantas disciplinas/módulos (as partes do curso) precisam estar concluídos, em contagem, nunca em %.
     * Uma parte está concluída com todas as suas aulas publicadas feitas. Maior que o total de hoje vale "todas".
     * Quando existe, vale no lugar de `minAulasPct`.
     */
    minPartes: z.optional(z.int().check(z.gte(0))),
    /** a operação confere a documentação de cada aluno antes da emissão (Certificação) */
    documentos: z.optional(z.boolean()),
    /** cada aluno precisa ser liberado pela operação, um a um ou em lote (Certificação) */
    liberacaoManual: z.optional(z.boolean()),
  }),
})
export type CertificacaoProduto = z.infer<typeof CertificacaoProduto>

/** O produto aberto no editor: o resumo da lista mais o passo Informações. */
export const ProdutoDetalhe = z.object({
  ...ProdutoResumo.shape,
  resumo: z.nullable(z.string()),
  sobre: z.nullable(z.string()),
  objetivo: z.nullable(z.string()),
  publicoAlvo: z.nullable(z.string()),
  cargaHorariaMin: z.nullable(z.int()),
  trilhas: z.array(z.string()),
  rotuloParte: z.string(),
  rotuloPartes: z.string(),
  capas: z.object({ larga: capa, quadro: capa, vertical: capa }),
  corDestaque: z.nullable(z.string()),
  sobreporOverlay: z.boolean(),
  slug: z.nullable(z.string()),
  /** a garantia adicional, em dias, além dos 7 incondicionais (PO, 21/10/2026); null = sem garantia adicional */
  garantiaDias: z.nullable(z.int()),
  certificacao: CertificacaoProduto,
  /** o que é próprio do tipo (docs/11), já com os padrões: veja `ConfigDoTipo` */
  config: z.unknown(),
  /** os itens da Biblioteca e os produtos que o config cita, para a tela mostrar o nome */
  refs: z.record(
    z.string(),
    z.object({ nome: z.string(), tipo: z.string(), cargaHorariaMin: z.nullable(z.int()), url: z.nullable(z.string()) }),
  ),
  criadoEm: z.string(),
})
export type ProdutoDetalhe = z.infer<typeof ProdutoDetalhe>

// ---------------------------------------------------------------- o próprio de cada tipo (docs/11)
const url = z
  .string()
  .check(z.trim(), z.maxLength(2000), z.regex(/^https:\/\/\S+$/, 'use um endereço https:// completo'))
const minutos = (min: number, max: number) => z.int().check(z.gte(min), z.lte(max))
/** Entregável: arquivos da gaveta Entregáveis, com o nome que o aluno vê. */
export const ConfigEntregavel = z.object({
  arquivos: z
    .array(z.object({ itemId: z.string(), rotulo: z.nullable(z.string().check(z.trim(), z.maxLength(120))) }))
    .check(z.maxLength(50)),
})
/** Conteúdo: seções com itens da gaveta Conteúdos, consumo livre, sem trilha nem certificado. */
export const ConfigConteudo = z.object({
  secoes: z
    .array(
      z.object({
        titulo: z.string().check(z.trim(), z.minLength(1, 'dê um nome à seção'), z.maxLength(120)),
        capaItemId: z.nullable(z.string()),
        itens: z
          .array(z.object({ itemId: z.string(), titulo: z.nullable(z.string().check(z.trim(), z.maxLength(200))) }))
          .check(z.maxLength(300)),
      }),
    )
    .check(z.maxLength(50)),
})
export const PLATAFORMAS_SALA = ['Google Meet', 'Zoom', 'Microsoft Teams', 'YouTube ao vivo', 'StreamYard'] as const
const onde = {
  linkExterno: z.nullable(url),
  plataforma: z.nullable(z.string().check(z.trim(), z.maxLength(60))),
}
/** Ao vivo: a live com data e vagas, ligada ou não a um curso. O acesso é o ingresso. */
export const ConfigAoVivo = z.object({
  inicioEm: z.nullable(z.string()),
  duracaoMin: minutos(5, 1440),
  vagas: z.nullable(z.int().check(z.gte(1), z.lte(100_000))),
  formato: z.enum(['live', 'reuniao']),
  provedor: z.enum(['plataforma', 'externo']),
  ...onde,
  cursoId: z.nullable(z.string()),
})
/** Mentoria: sessões individuais em pacote. Quem conduz e a agenda são da operação. */
export const ConfigMentoria = z.object({
  sessoes: z.int().check(z.gte(1), z.lte(200)),
  duracaoMin: minutos(10, 480),
  provedor: z.enum(['plataforma', 'externo']),
  ...onde,
  orientacoes: z.nullable(z.string().check(z.trim(), z.maxLength(2000))),
})
/** Aula em grupo: turma pequena com encontros; as vagas limitam o acesso. */
export const ConfigAulaGrupo = z.object({
  vagas: z.nullable(z.int().check(z.gte(1), z.lte(10_000))),
  duracaoMin: minutos(10, 480),
  provedor: z.enum(['plataforma', 'externo']),
  ...onde,
})
/**
 * Certificação: o direito ao certificado vendido à parte, ou o certificado avulso (por prova
 * ou por comprovação, sempre de um curso). Nunca tem modelo próprio: modelo e regras são do
 * curso ou da formação. O escopo "os cursos de uma assinatura" chega com as ofertas.
 */
export const ESCOPOS_CERTIFICACAO = [
  {
    id: 'curso',
    rotulo: 'Um curso',
    texto: 'O certificado de um curso fixo. Ex.: a taxa de certificação da pós montada como um curso.',
    cobrancas: ['por_certificado'],
  },
  {
    id: 'qualquer',
    rotulo: 'Qualquer curso',
    texto: 'O certificado de um curso à escolha do aluno, na hora de emitir.',
    cobrancas: ['por_certificado'],
  },
  {
    id: 'lista',
    rotulo: 'Uma lista de cursos',
    texto: 'O certificado de cada curso de uma lista fixa. Ex.: Pacote Office.',
    cobrancas: ['por_certificado', 'passe'],
  },
  {
    id: 'formacao',
    rotulo: 'Uma formação',
    texto: 'O certificado da formação. Ex.: a taxa de certificação da graduação.',
    cobrancas: ['por_certificado'],
  },
] as const
export const ConfigCertificacao = z.object({
  origem: z.enum(['conclusao', 'prova', 'comprovacao']),
  escopo: z.enum(['curso', 'qualquer', 'lista', 'formacao']),
  cursoId: z.nullable(z.string()),
  cursoIds: z.array(z.string()).check(z.maxLength(200)),
  formacaoId: z.nullable(z.string()),
  /** por certificado: cada compra, uma emissão; passe: uma compra cobre a lista enquanto vale */
  cobranca: z.enum(['por_certificado', 'passe']),
})
/** Formação: cursos em sequência (obrigatório ou optativo) e a regra de conclusão. */
export const REGRAS_CONCLUSAO = [
  { id: 'todos', rotulo: 'Todos os cursos' },
  { id: 'x_de_n', rotulo: 'X de N cursos' },
  { id: 'obrigatorios_mais_n', rotulo: 'Obrigatórios + N optativos' },
] as const
export const ConfigFormacao = z.object({
  cursos: z.array(z.object({ cursoId: z.string(), obrigatorio: z.boolean() })).check(z.maxLength(200)),
  regra: z.enum(['todos', 'x_de_n', 'obrigatorios_mais_n']),
  regraN: z.nullable(z.int().check(z.gte(0), z.lte(200))),
})
export const CONFIG_DO_TIPO = {
  curso: null,
  formacao: ConfigFormacao,
  ebook: ConfigEntregavel,
  evento: ConfigAoVivo,
  mentoria: ConfigMentoria,
  aula_grupo: ConfigAulaGrupo,
  biblioteca: ConfigConteudo,
  certificacao: ConfigCertificacao,
} as const
export type ConfigDoTipo = {
  formacao: z.infer<typeof ConfigFormacao>
  ebook: z.infer<typeof ConfigEntregavel>
  evento: z.infer<typeof ConfigAoVivo>
  mentoria: z.infer<typeof ConfigMentoria>
  aula_grupo: z.infer<typeof ConfigAulaGrupo>
  biblioteca: z.infer<typeof ConfigConteudo>
  certificacao: z.infer<typeof ConfigCertificacao>
}
/** O ponto de partida de cada tipo: o que vale enquanto nada foi salvo. */
export const CONFIG_PADRAO: ConfigDoTipo = {
  formacao: { cursos: [], regra: 'todos', regraN: null },
  ebook: { arquivos: [] },
  evento: {
    inicioEm: null,
    duracaoMin: 60,
    vagas: null,
    formato: 'live',
    provedor: 'externo',
    linkExterno: null,
    plataforma: null,
    cursoId: null,
  },
  mentoria: { sessoes: 4, duracaoMin: 60, provedor: 'externo', linkExterno: null, plataforma: null, orientacoes: null },
  aula_grupo: { vagas: null, duracaoMin: 60, provedor: 'externo', linkExterno: null, plataforma: null },
  biblioteca: { secoes: [] },
  certificacao: {
    origem: 'conclusao',
    escopo: 'curso',
    cursoId: null,
    cursoIds: [],
    formacaoId: null,
    cobranca: 'por_certificado',
  },
}
const textoOpc = (max: number) => z.optional(z.nullable(z.string().check(z.trim(), z.maxLength(max))))
export const EditarProduto = z.object({
  certificacao: z.optional(CertificacaoProduto),
  /** o próprio do tipo, inteiro; o servidor valida pelo esquema do tipo (`CONFIG_DO_TIPO`) */
  config: z.optional(z.unknown()),
  titulo: z.optional(tituloProduto),
  modalidade: textoOpc(80),
  status: z.optional(StatusProduto),
  resumo: textoOpc(300),
  sobre: textoOpc(10_000),
  objetivo: textoOpc(4000),
  publicoAlvo: textoOpc(4000),
  cargaHorariaMin: z.optional(z.nullable(z.int().check(z.gte(1), z.lte(200_000)))),
  trilhas: z.optional(z.array(z.string().check(z.trim(), z.minLength(2), z.maxLength(60))).check(z.maxLength(10))),
  rotuloParte: z.optional(z.string().check(z.trim(), z.minLength(2), z.maxLength(40))),
  rotuloPartes: z.optional(z.string().check(z.trim(), z.minLength(2), z.maxLength(40))),
  capaLargaId: z.optional(z.nullable(z.string())),
  capaQuadroId: z.optional(z.nullable(z.string())),
  capaVerticalId: z.optional(z.nullable(z.string())),
  corDestaque: z.optional(z.nullable(z.string().check(z.regex(/^#[0-9a-fA-F]{6}$/, 'cor em hexadecimal')))),
  sobreporOverlay: z.optional(z.boolean()),
  /** a garantia adicional (dias): de 8 a 365; null = sem garantia adicional */
  garantiaDias: z.optional(
    z.nullable(
      z
        .int()
        .check(z.gte(8, 'a garantia adicional começa em 8 dias'), z.lte(365, 'a garantia adicional vai até 365 dias')),
    ),
  ),
  slug: z.optional(
    z.nullable(
      z
        .string()
        .check(
          z.trim(),
          z.regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'endereço: letras minúsculas, números e hífen'),
          z.maxLength(80),
        ),
    ),
  ),
})

// ---------------------------------------------------------------- catálogo (docs/08)
/** Modalidade, Trilha e o nome das partes: listas que criam o que faltar na hora (aw-ava contrato/curso.ts). */
export const TipoCatalogo = z.enum(['modalidade', 'trilha', 'parte'])
export type TipoCatalogo = z.infer<typeof TipoCatalogo>
export const ItemCatalogo = z.object({
  id: z.string(),
  tipo: TipoCatalogo,
  nome: z.string(),
  plural: z.nullable(z.string()),
  /** quantos produtos não arquivados usam */
  usos: z.int(),
})
export type ItemCatalogo = z.infer<typeof ItemCatalogo>
export const NovoItemCatalogo = z.object({
  tipo: TipoCatalogo,
  nome: z.string().check(z.trim(), z.minLength(2, 'nome curto demais'), z.maxLength(60)),
  plural: z.optional(z.nullable(z.string().check(z.trim(), z.minLength(2), z.maxLength(60)))),
})

/**
 * A trilha da vitrine (PO, 16/10/2026): um agrupamento de cursos, não uma formação. É o item do catálogo (tipo
 * `trilha`) que o curso cita pelo nome; aqui ela ganha o que a subvitrine mostra: página própria, capas, textos e ordem.
 */
export const SituacaoTrilha = z.enum(['rascunho', 'ativa', 'inativa'])
export type SituacaoTrilha = z.infer<typeof SituacaoTrilha>
/** Um curso dentro da trilha: o que a trilha diz dele e se ele é fixo. */
export const ItemDaTrilha = z.object({
  produtoId: z.string(),
  titulo: z.string(),
  slug: z.nullable(z.string()),
  modalidade: z.nullable(z.string()),
  horas: z.nullable(z.number()),
  /** rascunho, publicado ou arquivado: só o publicado vai à página e ao checkout */
  status: z.string(),
  descricao: z.nullable(z.string()),
  obrigatorio: z.boolean(),
})
export type ItemDaTrilha = z.infer<typeof ItemDaTrilha>
/**
 * O conteúdo adicional das páginas de trilha e combo (PO, 20/10/2026): seções de texto próprias (título e texto) e
 * perguntas frequentes.
 */
export const ConteudoAdicional = z.object({
  secoes: z
    .array(
      z.object({
        titulo: z.string().check(z.trim(), z.minLength(2), z.maxLength(80)),
        texto: z.string().check(z.trim(), z.minLength(2), z.maxLength(3000)),
      }),
    )
    .check(z.maxLength(8)),
  perguntas: z
    .array(
      z.object({
        pergunta: z.string().check(z.trim(), z.minLength(2), z.maxLength(200)),
        resposta: z.string().check(z.trim(), z.minLength(2), z.maxLength(2000)),
      }),
    )
    .check(z.maxLength(20)),
})
export type ConteudoAdicional = z.infer<typeof ConteudoAdicional>
export const CONTEUDO_VAZIO: ConteudoAdicional = { secoes: [], perguntas: [] }

export const TrilhaDaGestao = z.object({
  id: z.string(),
  /** rascunho não existe para o público; ativa está no ar; inativa fica "encerrada" */
  situacao: SituacaoTrilha,
  /** os cursos, na ordem, com a descrição própria de cada um */
  itens: z.array(ItemDaTrilha),
  /** quantas ofertas a trilha tem */
  ofertas: z.int(),
  nome: z.string(),
  slug: z.string(),
  /** a frase do cartão */
  resumo: z.nullable(z.string()),
  /** o texto da página da trilha */
  apresentacao: z.nullable(z.string()),
  paraQuem: z.nullable(z.string()),
  /** o que a pessoa passa a dominar */
  destino: z.nullable(z.string()),
  /** o bloco que fecha a página: título e texto (ex.: "Vá além dos pontos") */
  destaqueTitulo: z.nullable(z.string()),
  destaqueTexto: z.nullable(z.string()),
  corDestaque: z.nullable(z.string()),
  capas: z.object({ larga: capa, vertical: capa }),
  ordem: z.int(),
  /** aparece na vitrine (o filtro por área do catálogo segue valendo) */
  visivel: z.boolean(),
  /** quantos produtos publicados a trilha reúne */
  cursos: z.int(),
  conteudo: ConteudoAdicional,
})
export type TrilhaDaGestao = z.infer<typeof TrilhaDaGestao>
const textoTrilha = (max: number) => z.optional(z.nullable(z.string().check(z.trim(), z.maxLength(max))))
/** O curso a colocar na trilha (a lista inteira substitui a anterior, na ordem recebida). */
export const SalvarItemDaTrilha = z.object({
  produtoId: z.string().check(z.maxLength(64)),
  descricao: z.optional(z.nullable(z.string().check(z.trim(), z.maxLength(600)))),
  obrigatorio: z.optional(z.boolean()),
})
export const SalvarTrilha = z.object({
  situacao: z.optional(SituacaoTrilha),
  itens: z.optional(z.array(SalvarItemDaTrilha).check(z.maxLength(100))),
  nome: z.optional(z.string().check(z.trim(), z.minLength(2, 'nome curto demais'), z.maxLength(60))),
  slug: z.optional(
    z
      .string()
      .check(
        z.trim(),
        z.regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'endereço: letras minúsculas, números e hífen'),
        z.maxLength(80),
      ),
  ),
  resumo: textoTrilha(240),
  apresentacao: textoTrilha(3000),
  paraQuem: textoTrilha(1200),
  destino: textoTrilha(1200),
  destaqueTitulo: textoTrilha(80),
  destaqueTexto: textoTrilha(1200),
  conteudo: z.optional(ConteudoAdicional),
  corDestaque: z.optional(
    z.nullable(z.string().check(z.regex(/^#[0-9a-fA-F]{6}$/, 'cor em hexadecimal, como #12343B'))),
  ),
  capaLargaId: z.optional(z.nullable(z.string())),
  capaVerticalId: z.optional(z.nullable(z.string())),
  ordem: z.optional(z.int().check(z.gte(0), z.lte(10_000))),
  visivel: z.optional(z.boolean()),
})

// ---------------------------------------------------------------- estrutura do curso (docs/08, passo 2)
/** O tipo da aula sai do conteúdo principal; sem ele, a aula ainda não tem tipo. */
export const TipoAula = z.enum(['video', 'pdf', 'html', 'embed'])
export type TipoAula = z.infer<typeof TipoAula>
export const tipoDaAula = (t: TipoItem): TipoAula | null =>
  t === 'video_externo' || t === 'video_arquivo'
    ? 'video'
    : t === 'pdf'
      ? 'pdf'
      : t === 'html'
        ? 'html'
        : t === 'embed'
          ? 'embed'
          : null
const itemNaAula = z.object({ id: z.string(), nome: z.string(), tipo: TipoItem })
export const AulaPainel = z.object({
  id: z.string(),
  parteId: z.string(),
  titulo: z.string(),
  descricao: z.nullable(z.string()),
  tipo: z.nullable(TipoAula),
  status: z.enum(['publicada', 'rascunho']),
  duracaoSeg: z.nullable(z.int()),
  principal: z.nullable(itemNaAula),
  anexos: z.array(itemNaAula),
})
export type AulaPainel = z.infer<typeof AulaPainel>
export const PartePainel = z.object({
  id: z.string(),
  titulo: z.string(),
  descricao: z.nullable(z.string()),
  cargaHorariaMin: z.nullable(z.int()),
  aulas: z.array(AulaPainel),
})
export type PartePainel = z.infer<typeof PartePainel>
export const EstruturaCurso = z.object({ partes: z.array(PartePainel) })
export type EstruturaCurso = z.infer<typeof EstruturaCurso>
export const SalvarParte = z.object({
  titulo: z.string().check(z.trim(), z.minLength(1, 'dê um título'), z.maxLength(200)),
  descricao: z.optional(z.nullable(z.string().check(z.trim(), z.maxLength(300)))),
  cargaHorariaMin: z.optional(z.nullable(z.int().check(z.gte(1), z.lte(100_000)))),
})
export const SalvarAula = z.object({
  titulo: z.string().check(z.trim(), z.minLength(1, 'dê um título'), z.maxLength(200)),
  descricao: z.optional(z.nullable(z.string().check(z.maxLength(10_000)))),
  parteId: z.string(),
  status: z.optional(z.enum(['publicada', 'rascunho'])),
  duracaoSeg: z.optional(z.nullable(z.int().check(z.gte(0), z.lte(86_400)))),
  principalItemId: z.optional(z.nullable(z.string())),
  anexosItemIds: z.optional(z.array(z.string()).check(z.maxLength(30))),
})
export const Ordem = z.object({ ids: z.array(z.string()).check(z.maxLength(500)) })
export const CopiarParte = z.object({ parteId: z.string() })
/** Onde um item da Biblioteca está em uso (o aviso antes de apagar). */
export const UsoDoItem = z.object({
  aulaId: z.string(),
  aulaTitulo: z.string(),
  cursoId: z.string(),
  cursoTitulo: z.string(),
})

// ---------------------------------------------------------------- avaliações (docs/08, passo 3)
// Porte de aw-ava/contrato/avaliacao.ts, só a montagem. Saíram: cursos donos e
// compartilhamento do banco (o banco é da instituição), questões de concurso (serviço,
// depois), prova reaproveitada entre cursos (depois) e a aplicação (janela de datas,
// código do fiscal, tentativas e notas), que é operação.
export const Dificuldade = z.enum(['facil', 'media', 'dificil'])
export type Dificuldade = z.infer<typeof Dificuldade>
export const UsoAvaliacao = z.enum(['prova', 'quiz'])
export type UsoAvaliacao = z.infer<typeof UsoAvaliacao>
/** questões (prova e quiz) ou o arquivo do aluno (atividade por envio, docs/15) */
export const FormatoAvaliacao = z.enum(['questoes', 'envio'])
export type FormatoAvaliacao = z.infer<typeof FormatoAvaliacao>
export const FORMATOS_ARQUIVO = ['pdf', 'docx', 'odt', 'rtf', 'txt', 'md'] as const
export const FormatoArquivo = z.enum(FORMATOS_ARQUIVO)
export type FormatoArquivo = z.infer<typeof FormatoArquivo>
/** Uma lauda: 1.250 caracteres com espaços (o padrão editorial mais usado). */
export const CARACTERES_POR_LAUDA = 1250
/** O resultado da pré-avaliação: cada critério com o veredito e o porquê, e uma nota sugerida. */
export const ResultadoPreAvaliacao = z.object({
  criterios: z.array(
    z.object({
      criterio: z.string(),
      atende: z.enum(['sim', 'parcial', 'nao']),
      comentario: z.string(),
      /** conferido por conta, não pela IA (mínimo de palavras e laudas) */
      exato: z.optional(z.boolean()),
    }),
  ),
  notaSugerida: z.nullable(z.number()),
  resumo: z.nullable(z.string()),
  modelo: z.nullable(z.string()),
})
export type ResultadoPreAvaliacao = z.infer<typeof ResultadoPreAvaliacao>
export const PreAvaliacaoCfg = z.object({
  ativa: z.boolean(),
  minPalavras: z.nullable(z.int().check(z.gte(1), z.lte(100_000))),
  minLaudas: z.nullable(z.number().check(z.gt(0), z.lte(500))),
  /** segue a estrutura de redação (introdução, desenvolvimento, conclusão)? */
  redacao: z.boolean(),
  /** tem os elementos de um artigo na ABNT? */
  abnt: z.boolean(),
  /** do que se trata a atividade e os critérios, em palavras de quem monta */
  criterios: z.nullable(z.string().check(z.maxLength(4000))),
})
export type PreAvaliacaoCfg = z.infer<typeof PreAvaliacaoCfg>
export const ConfigEnvio = z.object({
  formatos: z.array(FormatoArquivo).check(z.minLength(1, 'aceite ao menos um formato')),
  tamanhoMaxMb: z.int().check(z.gte(1), z.lte(50)),
  /** AAAA-MM-DD; depois do dia, não aceita mais */
  prazo: z.nullable(z.string().check(z.regex(/^\d{4}-\d{2}-\d{2}$/))),
  /** antes da correção, o aluno pode trocar o arquivo */
  reenvio: z.boolean(),
  pre: PreAvaliacaoCfg,
})
export type ConfigEnvio = z.infer<typeof ConfigEnvio>
export const CONFIG_ENVIO_PADRAO: ConfigEnvio = {
  formatos: ['pdf', 'docx', 'odt', 'txt'],
  tamanhoMaxMb: 10,
  prazo: null,
  reenvio: true,
  pre: { ativa: false, minPalavras: null, minLaudas: null, redacao: false, abnt: false, criterios: null },
}

export const Banco = z.object({
  id: z.string(),
  nome: z.string(),
  descricao: z.nullable(z.string()),
  origem: z.nullable(z.string()),
  /** questões ativas (as que entram no sorteio) */
  totalQuestoes: z.int(),
  /** avaliações que sorteiam deste banco */
  usadoEm: z.int(),
  criadoEm: z.string(),
})
export type Banco = z.infer<typeof Banco>
export const BancoEntrada = z.object({
  nome: z.string().check(z.trim(), z.minLength(2, 'nome curto demais'), z.maxLength(120)),
  descricao: z.optional(z.nullable(z.string().check(z.maxLength(1000)))),
  origem: z.optional(z.nullable(z.string().check(z.maxLength(200)))),
})

export const AlternativaEntrada = z.object({
  texto: z.string().check(z.trim(), z.minLength(1), z.maxLength(2000)),
  correta: z.boolean(),
})
export const StatusQuestao = z.enum(['ativa', 'rascunho', 'arquivada'])
export const QuestaoEntrada = z.object({
  enunciado: z.string().check(z.trim(), z.minLength(3, 'enunciado curto demais'), z.maxLength(8000)),
  explicacao: z.optional(z.nullable(z.string().check(z.maxLength(4000)))),
  dificuldade: z.optional(Dificuldade),
  tema: z.optional(z.nullable(z.string().check(z.trim(), z.maxLength(80)))),
  etiquetas: z.optional(z.array(z.string().check(z.maxLength(60))).check(z.maxLength(20))),
  embaralharAlternativas: z.optional(z.boolean()),
  origem: z.optional(z.nullable(z.string().check(z.maxLength(200)))),
  status: z.optional(StatusQuestao),
  alternativas: z.array(AlternativaEntrada).check(
    z.minLength(2, 'pelo menos duas alternativas'),
    z.maxLength(10),
    z.refine((a) => a.filter((x) => x.correta).length === 1, 'exatamente uma alternativa correta'),
  ),
})
export type QuestaoEntrada = z.infer<typeof QuestaoEntrada>
export const Questao = z.object({
  id: z.string(),
  bancoId: z.string(),
  enunciado: z.string(),
  explicacao: z.nullable(z.string()),
  dificuldade: Dificuldade,
  tema: z.nullable(z.string()),
  etiquetas: z.array(z.string()),
  embaralharAlternativas: z.boolean(),
  origem: z.nullable(z.string()),
  status: StatusQuestao,
  alternativas: z.array(z.object({ id: z.string(), texto: z.string(), correta: z.boolean() })),
  criadaEm: z.string(),
})
export type Questao = z.infer<typeof Questao>
export const ListaQuestoes = z.object({
  banco: z.object({ id: z.string(), nome: z.string() }),
  total: z.int(),
  pagina: z.int(),
  porPagina: z.int(),
  itens: z.array(Questao),
  temas: z.array(z.object({ tema: z.string(), n: z.int() })),
})
/** Importação: as questões já no formato daqui (o app converte GIFT, Moodle XML e planilha). */
export const QuestoesImportacao = z.object({
  questoes: z.array(QuestaoEntrada).check(z.minLength(1), z.maxLength(1000)),
  origem: z.optional(z.string().check(z.maxLength(200))),
})

export const RegraSorteio = z.object({
  bancoId: z.string(),
  quantidade: z.int().check(z.gte(1), z.lte(200)),
  tema: z.optional(z.nullable(z.string())),
  dificuldade: z.optional(z.nullable(Dificuldade)),
})
export type RegraSorteio = z.infer<typeof RegraSorteio>
const camposAvaliacao = {
  uso: UsoAvaliacao,
  titulo: z.string().check(z.trim(), z.minLength(2, 'dê um título'), z.maxLength(200)),
  descricao: z.nullable(z.string().check(z.maxLength(4000))),
  parteId: z.nullable(z.string()),
  aulaId: z.nullable(z.string()),
  regras: z.array(RegraSorteio).check(z.maxLength(20)),
  embaralharQuestoes: z.boolean(),
  embaralharAlternativas: z.boolean(),
  peso: z.number().check(z.gte(0), z.lte(100)),
  notaMinima: z.nullable(z.number().check(z.gte(0), z.lte(10))),
  tempoLimiteMin: z.nullable(z.int().check(z.gte(1), z.lte(600))),
  tentativasMax: z.nullable(z.int().check(z.gte(1), z.lte(100))),
  intervaloTentativasMin: z.nullable(z.int().check(z.gte(0), z.lte(100_000))),
  notaVale: z.enum(['maior', 'ultima', 'media']),
  revisao: z.enum(['so_nota', 'nota_e_gabarito', 'gabarito_apos_fechar']),
  registrarEventos: z.boolean(),
  /** concluir libera a próxima aula */
  liberaProxima: z.boolean(),
  /** trava o certificado se não concluída (entra na regra de liberação do passo 4) */
  travaCertificado: z.boolean(),
  formato: z.optional(FormatoAvaliacao),
  envio: z.optional(ConfigEnvio),
}
export const AvaliacaoEntrada = z.object(camposAvaliacao)
export type AvaliacaoEntrada = z.infer<typeof AvaliacaoEntrada>
/** Copiar atividades de outro curso (item 2 da rodada de 19/10/2026): entram como rascunho, no fim do curso. */
export const CopiarAvaliacoes = z.object({
  produtoId: z.string(),
  ids: z.array(z.string()).check(z.minLength(1, 'escolha ao menos uma'), z.maxLength(30)),
})
export const StatusAvaliacao = z.enum(['rascunho', 'publicada', 'arquivada'])
export const AvaliacaoPainel = z.object({
  id: z.string(),
  ...camposAvaliacao,
  status: StatusAvaliacao,
  totalQuestoes: z.int(),
  atualizadaEm: z.string(),
})
export type AvaliacaoPainel = z.infer<typeof AvaliacaoPainel>

// ---------------------------------------------------------------- Smart Itens (docs/08, passo 5)
/** Serviços premium da instância, ligados pelo superadmin (geram custo). */
export const ServicosInstancia = z.object({ smartItens: z.boolean() })
export type ServicosInstancia = z.infer<typeof ServicosInstancia>

export const TIPOS_SMART = ['resumo', 'glossario', 'flashcards', 'duvidas', 'questoes'] as const
export const TipoSmart = z.enum(TIPOS_SMART)
export type TipoSmart = z.infer<typeof TipoSmart>
const curto = z.string().check(z.trim(), z.minLength(1), z.maxLength(2000))
export const ConteudoResumo = z.object({
  paragrafos: z.array(curto).check(z.maxLength(6)),
  pontosChave: z.array(curto).check(z.maxLength(10)),
})
export const ConteudoGlossario = z.object({
  termos: z.array(z.object({ termo: curto, definicao: curto })).check(z.maxLength(200)),
})
export const ConteudoFlashcards = z.object({
  cartas: z.array(z.object({ frente: curto, verso: curto })).check(z.maxLength(200)),
})
export const ConteudoDuvidas = z.object({
  itens: z.array(z.object({ pergunta: curto, resposta: curto })).check(z.maxLength(20)),
})
export const ConteudoQuestoes = z.object({
  questoes: z
    .array(
      z.object({
        enunciado: curto,
        alternativas: z.array(z.object({ texto: curto, correta: z.boolean() })).check(z.minLength(2), z.maxLength(6)),
        explicacao: z.optional(z.nullable(z.string())),
        dificuldade: z.optional(Dificuldade),
      }),
    )
    .check(z.maxLength(200)),
})
export const ESQUEMA_SMART = {
  resumo: ConteudoResumo,
  glossario: ConteudoGlossario,
  flashcards: ConteudoFlashcards,
  duvidas: ConteudoDuvidas,
  questoes: ConteudoQuestoes,
} as const
export const SmartItem = z.object({
  tipo: TipoSmart,
  conteudo: z.unknown(),
  status: z.enum(['gerado', 'publicado']),
  modelo: z.nullable(z.string()),
  geradoEm: z.string(),
})
export type SmartItem = z.infer<typeof SmartItem>
export const EstadoSmart = z.object({
  /** o serviço está ligado para a instância (premium) */
  ligado: z.boolean(),
  /** o texto-base de cada aula: pronto, faltando (pede colar) ou velho (o conteúdo mudou) */
  aulas: z.array(
    z.object({
      id: z.string(),
      titulo: z.string(),
      parte: z.string(),
      texto: z.enum(['pronto', 'faltando', 'velho']),
      origem: z.nullable(z.enum(['pdf', 'html', 'colado'])),
      /** dá para extrair sozinho (o conteúdo é PDF ou HTML) */
      extraivel: z.boolean(),
    }),
  ),
  itens: z.array(SmartItem),
})
export type EstadoSmart = z.infer<typeof EstadoSmart>

// ---------------------------------------------------------------- área do aluno (docs/13)
const aulaNaTrilha = z.object({
  id: z.string(),
  titulo: z.string(),
  /** o tipo do conteúdo principal (vídeo, pdf, html, embed); sem conteúdo, nulo */
  tipo: z.nullable(TipoAula),
  duracaoSeg: z.nullable(z.int()),
  feita: z.boolean(),
})
const parteNaTrilha = z.object({ id: z.string(), titulo: z.string(), aulas: z.array(aulaNaTrilha) })
const materialDoAluno = z.object({
  id: z.string(),
  nome: z.string(),
  tipo: TipoItem,
  tamanho: z.nullable(z.int()),
  url: z.string(),
})
export const CursoDoAluno = z.object({
  id: z.string(),
  tipo: TipoProduto,
  titulo: z.string(),
  modalidade: z.nullable(z.string()),
  resumo: z.nullable(z.string()),
  sobre: z.nullable(z.string()),
  objetivo: z.nullable(z.string()),
  cargaHorariaMin: z.nullable(z.int()),
  capa: z.nullable(z.string()),
  rotuloParte: z.string(),
  rotuloPartes: z.string(),
  /** até quando vale o acesso; nulo = sem prazo (ou quem vê é da equipe) */
  validoAte: z.nullable(z.string()),
  progresso: z.object({ aulas: z.int(), concluidas: z.int() }),
  continuar: z.nullable(
    z.object({ aulaId: z.string(), titulo: z.string(), parte: z.string(), tipo: z.nullable(TipoAula) }),
  ),
  partes: z.array(parteNaTrilha),
  materiais: z.array(materialDoAluno),
  /** o que o Smart Itens publicou para o curso */
  smart: z.object({
    resumo: z.nullable(ConteudoResumo),
    glossario: z.nullable(ConteudoGlossario),
    flashcards: z.nullable(ConteudoFlashcards),
    duvidas: z.nullable(ConteudoDuvidas),
  }),
  provas: z.array(
    z.object({
      id: z.string(),
      titulo: z.string(),
      parte: z.nullable(z.string()),
      notaMinima: z.nullable(z.number()),
      tentativasMax: z.nullable(z.int()),
      tentativas: z.int(),
      melhorNota: z.nullable(z.number()),
      aprovada: z.boolean(),
      travaCertificado: z.boolean(),
      /** questões (prova) ou envio de arquivo (atividade) */
      formato: FormatoAvaliacao,
      /** atividade: a última entrega espera correção */
      aguardando: z.boolean(),
    }),
  ),
  certificado: z.object({
    emite: z.boolean(),
    /** o que ainda falta para emitir */
    faltam: z.array(z.string()),
    /** já emitido: o código de validação */
    codigo: z.nullable(z.string()),
  }),
})
export type CursoDoAluno = z.infer<typeof CursoDoAluno>
export const AulaDoAluno = z.object({
  id: z.string(),
  titulo: z.string(),
  descricao: z.nullable(z.string()),
  duracaoSeg: z.nullable(z.int()),
  parte: z.object({ id: z.string(), titulo: z.string() }),
  numero: z.int(),
  totalNaParte: z.int(),
  /** o conteúdo principal: o tipo e o endereço (o vídeo por link já vem pronto para embutir) */
  conteudo: z.nullable(
    z.object({ tipo: TipoItem, url: z.string(), nome: z.string(), embutir: z.nullable(z.string()) }),
  ),
  anexos: z.array(materialDoAluno),
  feita: z.boolean(),
  anterior: z.nullable(z.object({ id: z.string(), titulo: z.string() })),
  proxima: z.nullable(z.object({ id: z.string(), titulo: z.string() })),
  curso: z.object({ id: z.string(), titulo: z.string(), aulas: z.int(), concluidas: z.int(), rotuloParte: z.string() }),
  trilha: z.array(parteNaTrilha),
})
export type AulaDoAluno = z.infer<typeof AulaDoAluno>

export const ProvaDoAluno = z.object({
  id: z.string(),
  titulo: z.string(),
  descricao: z.nullable(z.string()),
  notaMinima: z.nullable(z.number()),
  tempoLimiteMin: z.nullable(z.int()),
  tentativasMax: z.nullable(z.int()),
  /** a tentativa aberta (questões sem a resposta); nula quando não há tentativa em curso */
  tentativa: z.nullable(
    z.object({
      id: z.string(),
      iniciadaEm: z.string(),
      questoes: z.array(
        z.object({
          id: z.string(),
          enunciado: z.string(),
          alternativas: z.array(z.object({ id: z.string(), texto: z.string() })),
        }),
      ),
    }),
  ),
  historico: z.array(z.object({ nota: z.nullable(z.number()), entregueEm: z.nullable(z.string()) })),
  /** pode começar outra tentativa agora */
  podeTentar: z.boolean(),
  motivo: z.nullable(z.string()),
})
export type ProvaDoAluno = z.infer<typeof ProvaDoAluno>
export const ResultadoDaProva = z.object({
  nota: z.number(),
  acertos: z.int(),
  total: z.int(),
  aprovada: z.boolean(),
  /** conforme a revisão da prova: vazio quando só a nota volta */
  gabarito: z.array(
    z.object({
      questaoId: z.string(),
      correta: z.nullable(z.string()),
      marcada: z.nullable(z.string()),
      acertou: z.boolean(),
      explicacao: z.nullable(z.string()),
    }),
  ),
})
export type ResultadoDaProva = z.infer<typeof ResultadoDaProva>
export const DuvidaDoAluno = z.object({
  id: z.string(),
  texto: z.string(),
  aula: z.nullable(z.object({ id: z.string(), titulo: z.string() })),
  criadaEm: z.string(),
  resposta: z.nullable(z.string()),
  respondidaPor: z.nullable(z.string()),
  respondidaEm: z.nullable(z.string()),
})
export type DuvidaDoAluno = z.infer<typeof DuvidaDoAluno>
export const NovaDuvida = z.object({
  texto: z.string().check(z.trim(), z.minLength(5, 'escreva a dúvida'), z.maxLength(4000)),
  aulaId: z.optional(z.nullable(z.string())),
})
/** O selo de autenticidade do verso: o que prova a emissão e leva à validação pública. */
export const SeloDoCertificado = z.object({
  codigo: z.string(),
  /** o endereço da validação pública (o que o QR abre) */
  url: z.string(),
  /** SHA-256 dos dados congelados na emissão (a impressão digital) */
  hash: z.string(),
  emitidoEm: z.string(),
  instituicao: z.object({ nome: z.string(), logoUrl: z.nullable(z.string()) }),
  /** certificado formal: o lugar no livro de registro */
  registro: z.optional(z.nullable(z.object({ numero: z.int(), livro: z.int(), folha: z.int() }))),
})
export type SeloDoCertificado = z.infer<typeof SeloDoCertificado>
/** O verso existe se o modelo desenhou nele, se tem arte de verso ou se o selo de autenticidade está ligado. */
export const temVersoNoCertificado = (layout: LayoutCertificado, fundoVerso: string | null, selo: unknown) =>
  layout.elementos.some((e) => e.pagina === 'verso') || !!fundoVerso || (!!selo && layout.selo !== false)

export const CertificadoEmitido = z.object({
  codigo: z.string(),
  /** a versão do registro (a correção de um dado gera a seguinte) */
  versao: z.optional(z.int()),
  curso: z.string(),
  nome: z.string(),
  emitidoEm: z.string(),
  revogado: z.boolean(),
  selo: z.optional(z.nullable(SeloDoCertificado)),
  dados: z.object({
    /** a versão da grade do curso em que a pessoa entrou */
    matrizVersao: z.optional(z.int()),
    valores: z.record(z.string(), z.string()),
    layout: LayoutCertificado,
    fundoFrente: z.nullable(z.string()),
    fundoVerso: z.nullable(z.string()),
    disciplinas: z.array(
      z.object({
        titulo: z.string(),
        cargaHorariaMin: z.nullable(z.number()),
        nota: z.nullable(z.number()),
        situacao: z.string(),
        docenteNome: z.nullable(z.string()),
        docenteTitulacao: z.nullable(z.string()),
        inicioEm: z.nullable(z.string()),
        conclusaoEm: z.nullable(z.string()),
      }),
    ),
  }),
})
export type CertificadoEmitido = z.infer<typeof CertificadoEmitido>

// ---------------------------------------------------------------- atividade por envio (docs/15)
export const SituacaoEntrega = z.enum(['enviada', 'substituida', 'devolvida', 'corrigida'])
export type SituacaoEntrega = z.infer<typeof SituacaoEntrega>
export const EntregaDoAluno = z.object({
  id: z.string(),
  arquivo: z.string(),
  bytes: z.int(),
  enviadaEm: z.string(),
  situacao: SituacaoEntrega,
  nota: z.nullable(z.number()),
  comentario: z.nullable(z.string()),
  corrigidaEm: z.nullable(z.string()),
})
export type EntregaDoAluno = z.infer<typeof EntregaDoAluno>
export const AtividadeDoAluno = z.object({
  id: z.string(),
  titulo: z.string(),
  descricao: z.nullable(z.string()),
  notaMinima: z.nullable(z.number()),
  formatos: z.array(FormatoArquivo),
  tamanhoMaxMb: z.int(),
  prazo: z.nullable(z.string()),
  reenvio: z.boolean(),
  entregas: z.array(EntregaDoAluno),
  podeEnviar: z.boolean(),
  motivo: z.nullable(z.string()),
})
export type AtividadeDoAluno = z.infer<typeof AtividadeDoAluno>

// ---------------------------------------------------------------- textos do produto com IA
/** Os textos do produto que a IA corrige ou escreve, com o limite de cada um. */
export const CAMPOS_TEXTO_IA = {
  titulo: 120,
  resumo: 300,
  sobre: 1500,
  objetivo: 800,
  publicoAlvo: 600,
  destaqueTitulo: 80,
  destaqueTexto: 1200,
  // atividades do curso (prova, quiz e trabalho com envio): o mesmo formulário, três campos de texto livre
  avaliacaoTitulo: 120,
  avaliacaoInstrucoes: 1500,
  avaliacaoEnunciado: 2000,
  // editor de questões do banco: o texto vem da pessoa; a IA nunca decide a alternativa correta nem a dificuldade
  questaoEnunciado: 2000,
  questaoAlternativa: 400,
  questaoExplicacao: 1500,
} as const
export const CampoTextoIa = z.enum([
  'titulo',
  'resumo',
  'sobre',
  'objetivo',
  'publicoAlvo',
  'destaqueTitulo',
  'destaqueTexto',
  'avaliacaoTitulo',
  'avaliacaoInstrucoes',
  'avaliacaoEnunciado',
  'questaoEnunciado',
  'questaoAlternativa',
  'questaoExplicacao',
])
export type CampoTextoIa = z.infer<typeof CampoTextoIa>
export const PedidoTextoIa = z.object({
  campo: CampoTextoIa,
  /** corrigir o texto atual, ou escrever a partir de uma ideia curta */
  acao: z.enum(['corrigir', 'escrever']),
  texto: z.string().check(z.maxLength(4000)),
  ideia: z.optional(z.string().check(z.maxLength(1000))),
  /** o que já está escrito nos outros campos (o formulário ainda não salvo) */
  contexto: z.optional(z.record(z.string(), z.string().check(z.maxLength(4000)))),
})
export const SugestaoTextoIa = z.object({ texto: z.string(), mudancas: z.array(z.string()) })
export type SugestaoTextoIa = z.infer<typeof SugestaoTextoIa>
